import { useEffect, useState, useRef } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  useLocation,
} from "react-router-dom";
import "./App.css";
import Home from "./pages/Home";
import GroupIcon from "@mui/icons-material/Group";
import HomeIcon from "@mui/icons-material/Home";
import StoreFrontIcon from "@mui/icons-material/Storefront";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CasinoIcon from "@mui/icons-material/Casino";
import PersonIcon from "@mui/icons-material/Person";
import Tasks from "./pages/Tasks";
import Profile from "./pages/Profile";
import Store from "./pages/Store";
import WebApp from "@twa-dev/sdk";
import axios from "axios";
import { API_URL } from "./config";
import BannedMessage from "./components/BannedMessage";
import Game from "./pages/Game";
import SpinGame from "./pages/SpinGame";
import CoinFlipGame from "./pages/CoinFlipGame";
import MysteryChestGame from "./pages/MysteryChestGame";
import ScratchGame from "./pages/ScratchGame";
import FootballPredictionGame from "./pages/FootballPredictionGame";
import Miner from "./pages/Miner";
import Loading from "./components/Loading";
import TermsAndConditions from "./components/TermsAndConditions";
import PurchasedMovies from "./pages/PurchasedMovies";
import ReactGA from "react-ga4";
import { motion, AnimatePresence } from "framer-motion";
import { UserProvider, useUser } from "./context/UserContext";
import Header from "./components/Header";
import { getDisplayName } from "./utils/getDisplayName";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { TonConnectUIProvider } from "@tonconnect/ui-react";

ReactGA.initialize("G-T4PQR5J1XE");

function App() {
  return (
    <TonConnectUIProvider manifestUrl="https://biscopestore-0217a6e04cd0.herokuapp.com/tonconnect-manifest.json">
      <UserProvider>
        <AppContent />
      </UserProvider>
    </TonConnectUIProvider>
  );
}

function AppContent() {
  const { userData, setUserData, loading, isBanned, setIsBanned, isTelegramMiniApp, APIKey } = useUser();
  const [isPopUp, setIsPopUp] = useState(false);

  useEffect(() => {
    const requestPermissions = async () => {
      try {
        await WebApp.requestPermissions({
          permissions: ["write", "read"],
        });
      } catch (error) {
        console.error("Permission request failed", error);
      }
    };
    requestPermissions();
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://js.onclckmn.com/static/onclicka.js";
    script.dataset.admpid = "270297";
    script.async = true;
    document.head.appendChild(script);
  }, []);

  const didCheckPopup = useRef(false);

  // Check banned & popup status once — on first load only
  useEffect(() => {
    if (!loading && userData.user && !didCheckPopup.current) {
      didCheckPopup.current = true;
      setIsBanned(userData.user.isBanned || false);
      setIsPopUp(userData.user.isPopUp || false);
    }
  }, [loading, userData.user]);

  // Handle popup logic in an effect to avoid render loops
  useEffect(() => {
    if (isPopUp) {
      WebApp.showPopup(
        {
          title: "Gems are adjusted",
          message:
            "You violated our rules and your gems have been adjusted. This decision is final and we will not discuss anymore. Sorry for the inconvenience caused.",
          buttons: [{ id: "Ok", type: "default", text: "OK" }],
        },
        (buttonId) => {
          if (buttonId === "Ok") {
            // Optional: window.location.href = "/";
            // We can just let them continue using the app
          }
        }
      );

      // Turn off popup in the backend via dedicated endpoint
      const currentUserId = userData.userId || userData.user?.userId;
      if (currentUserId) {
        console.log(`[CLIENT] Attempting to clear popup for userId: ${currentUserId}`);
        axios.post(
          `${API_URL}/api/user/${currentUserId}/clearPopup`,
          { initData: WebApp.initData },
          {
            params: { initData: WebApp.initData },
            headers: { "x-api-key": APIKey },
          }
        ).then(() => {
          console.log("[CLIENT] successfully cleared popup on backend");
        }).catch(err => {
          console.error("[CLIENT] Failed to update popup status", err);
        });
      } else {
        console.warn("[CLIENT] Cannot clear popup, currentUserId is undefined!", userData);
      }

      // Immediately clear local state so popup doesn't reopen
      setIsPopUp(false);
      setUserData(prev => ({ ...prev, user: { ...(prev.user || {}), isPopUp: false } }));
    }
  }, [isPopUp, APIKey, setUserData]); // Removed userData from deps to prevent re-triggering

  if (loading) {
    return <Loading />;
  }

  if (isBanned) {
    return <BannedMessage />;
  }

  if (userData.user && userData.user.acceptedTerms === false) {
    return <TermsAndConditions />;
  }

  if (!isTelegramMiniApp && !loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-800 text-white">
        <h1 className="text-2xl">
          Please open this page in the Telegram app to continue
        </h1>
      </div>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <RouteChangeTracker />
      <div className="App bg-slate-50 dark:bg-gray-800 transition-colors duration-300 min-h-screen flex flex-col">
        <Header
          points={userData.user?.points || 0}
          photo_url={userData.photo_url}
          name={getDisplayName(userData)}
          tickets={userData.user?.tickets || 0}
        />
        <AnimatedRoutes />

        <Footer />
        <ToastContainer 
          position="bottom-center"
          autoClose={5000}
          limit={2}
          style={{ marginBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))', zIndex: 10005 }}
          className="!z-[10005]"
        />
      </div>
    </Router>
  );
}

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <main className="flex-grow">
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<Home />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/store" element={<Store />} />
          <Route path="/game" element={<Game />} />
          <Route path="/game/spin" element={<SpinGame />} />
          <Route path="/game/coinflip" element={<CoinFlipGame />} />
          <Route path="/game/chest" element={<MysteryChestGame />} />
          <Route path="/game/scratch" element={<ScratchGame />} />
          <Route path="/game/football" element={<FootballPredictionGame />} />
          <Route path="/miner" element={<Miner />} />
          <Route path="/purchased" element={<PurchasedMovies />} />
          <Route path="/purchased-movies" element={<PurchasedMovies />} />
        </Routes>
      </AnimatePresence>
    </main>
  );
};

const ScrollToTop = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
};

const RouteChangeTracker = () => {
  const location = useLocation();
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname });
  }, [location.pathname]);
  return null;
};

const Footer = () => {
  const location = useLocation();
  const { userData } = useUser();
  const unclaimedBetsCount = userData?.user?.unclaimedBetsCount || 0;

  const tabs = [
    { path: "/", icon: HomeIcon, label: "Home" },
    { path: "/tasks", icon: AssignmentTurnedInIcon, label: "Tasks" },
    { path: "/store", icon: StoreFrontIcon, label: "Store" },
    { path: "/game", icon: CasinoIcon, label: "Game" },
    { path: "/profile", icon: PersonIcon, label: "Profile" },
  ];

  return (
    <div className="navBar fixed bottom-4 left-0 right-0 flex justify-center z-50">
      <nav className="w-11/12 max-w-[450px]">
        <div
          className="relative rounded-3xl overflow-hidden h-[68px] bg-white/80 dark:bg-white/5 border border-slate-200/60 dark:border-white/[0.08] shadow-lg dark:shadow-[0_8px_32px_rgba(0,0,0,0.25)]"
          style={{
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
          }}
        >
          <div className="flex justify-around items-center h-full relative z-20 px-1">
            {tabs.map((tab) => {
              const isActive = location.pathname === tab.path;
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  className={`relative flex flex-col items-center justify-center w-full h-full gap-0.5 transition-all duration-300 ease-in-out ${
                    tab.label === "Tasks" ? "tasksTab"
                    : tab.label === "Store" ? "storeTab"
                    : tab.label === "Game" ? "gameTab"
                    : tab.label === "Profile" ? "profileTab"
                    : "homeTab"
                  }`}
                >
                  {/* Active pill glow behind icon */}
                  {isActive && (
                    <motion.div
                      layoutId="activePill"
                      className="absolute inset-x-1.5 inset-y-1.5 rounded-2xl"
                      style={{
                        background: "linear-gradient(135deg, rgba(192,132,252,0.3), rgba(139,92,246,0.2))",
                        boxShadow: "0 0 20px rgba(192,132,252,0.35), inset 0 1px 0 rgba(255,255,255,0.15)"
                      }}
                      transition={{ type: "spring", stiffness: 350, damping: 30 }}
                    />
                  )}

                  {/* Icon */}
                  <motion.div
                    animate={{ scale: isActive ? 1.18 : 1, y: isActive ? -1 : 0 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    className="relative z-10"
                  >
                    <tab.icon
                      className={`text-[22px] transition-colors duration-300 ${
                        isActive
                          ? "text-fuchsia-400 drop-shadow-[0_0_6px_rgba(232,121,249,0.7)]"
                          : "text-slate-500 dark:text-white/40"
                      }`}
                    />
                    {tab.label === "Game" && unclaimedBetsCount > 0 && (
                      <span className="absolute -top-1 -right-2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-md animate-pulse z-20 border border-white dark:border-slate-800">
                        {unclaimedBetsCount}
                      </span>
                    )}
                  </motion.div>

                  {/* Label */}
                  <motion.span
                    animate={{ opacity: isActive ? 1 : 0.6, y: isActive ? 0 : 1 }}
                    transition={{ duration: 0.2 }}
                    className={`text-[10px] font-semibold tracking-wide relative z-10 ${
                      isActive
                        ? "text-fuchsia-400"
                        : "text-slate-500 dark:text-white/40"
                    }`}
                  >
                    {tab.label}
                  </motion.span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>
    </div>
  );
};

export default App;
